import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Chinese mirror of the Thai power-of-attorney-abroad guide. */
const TITLE = "泰国委托书（Power of Attorney）出境使用 — 如何拟定不被退件";
const DESC =
  "出境使用的泰国委托书应如何拟定：授权范围的写法、认证顺序，以及最常导致重写与退件的错误。";
const PATH = "/blog/power-of-attorney-abroad-guide";
const URL = `/zh${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "委托书需要由律师起草吗？",
    answer:
      "法律上不强制，但受理机关对授权范围与格式的要求往往很具体。由熟悉目的地要求的律师起草，可降低范围不足或表述含糊而被退件的风险。",
  },
  {
    question: "授权范围应该写多宽？",
    answer:
      "以完成事项所需为限，逐项具体列明（例如签署特定合同、领取特定文件、代为申报）。过窄会办不成事，过宽则可能被机关质疑或被滥用。",
  },
  {
    question: "需要经过公证律师（Notarial Services Attorney）吗？",
    answer:
      "在国外使用的委托书通常需要先由具备公证服务资格的律师办理签署见证或宣誓证明，再进入后续认证程序。",
  },
  {
    question: "认证顺序是什么？",
    answer:
      "一般顺序为：律师公证 → 泰国外交部领事司认证 → 目的地国驻泰使领馆认证。顺序颠倒会被退回重办。",
  },
  {
    question: "委托书需要翻译吗？",
    answer:
      "视目的地要求而定。多数机关要求目的地语言版本或双语版本，且译文须完整包含印章与批注文字。",
  },
  {
    question: "委托书有有效期吗？",
    answer:
      "可在文中自行约定期限或完成事项后自动失效。若未约定，部分受理机关会以签署时间是否在数个月内为准。",
  },
  {
    question: "最常见的错误有哪些？",
    answer:
      "授权范围过于笼统、当事人资料与护照不一致、缺少见证或签署页、认证顺序错误，以及译文遗漏印章文字。",
  },
];

export const Route = createFileRoute("/zh/blog/power-of-attorney-abroad-guide")({
  component: ZhPoaGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "zh" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("power-of-attorney-abroad-guide", "zh") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "泰国委托书, power of attorney 泰国, 委托书公证, 委托书领事认证, 泰国授权书翻译",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "zh"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "首页", url: "/zh" },
              { name: "指南", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("power-of-attorney-abroad-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Notary Public",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const PARTS: Array<{ part: string; note: string }> = [
  { part: "当事人资料", note: "委托人与受托人的姓名拼写、证件号码须与护照一致" },
  { part: "授权事项", note: "逐项具体列明可代为办理的行为与对象" },
  { part: "授权范围限制", note: "明确不得转委托、不得处分资产等限制" },
  { part: "期限与失效条件", note: "约定有效期或完成事项后自动失效" },
  { part: "适用法律与语言", note: "说明适用法律，并指明以何种语言版本为准" },
  { part: "签署与见证", note: "签署日期、地点，以及公证律师的见证与印章" },
];

const STEPS: string[] = [
  "先取得受理机关对委托书内容与格式的书面要求",
  "依需求逐项拟定授权事项与限制条款",
  "核对当事人资料与护照拼写完全一致",
  "在公证律师面前签署并取得见证／宣誓证明",
  "办理泰国外交部领事司认证",
  "办理目的地国驻泰使领馆认证后再寄送或递交",
];

const MISTAKES: string[] = [
  "授权事项写得过于笼统，机关无法确认权限范围",
  "姓名拼写、证件号码与护照不符",
  "缺少签署见证或漏签署页",
  "认证顺序错误（先送使领馆再送领事司）",
  "译文遗漏印章、钢印或批注文字",
];

function ZhPoaGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/zh" className="text-muted-foreground hover:text-foreground">
          首页
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          指南
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="power-of-attorney-abroad-guide" locale="zh" />
      <p className="answer-summary speakable-summary">
        出境使用的泰国委托书须写明当事人资料、逐项授权事项、限制条款与有效期，并在公证律师面前签署见证。
        随后依序办理泰国外交部领事司认证与目的地国驻泰使领馆认证。授权范围含糊与认证顺序错误是最常见的退件原因。
      </p>

      <h2>1. 委托书应包含的部分</h2>
      <ul>
        {PARTS.map((p) => (
          <li key={p.part}>
            <strong>{p.part}：</strong> {p.note}
          </li>
        ))}
      </ul>

      <h2>2. 办理顺序</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 常见错误</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. 常见问题</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考来源与相关服务</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          泰国律师公会
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          泰国外交部领事司
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/zh/notary" className="text-primary underline">
            公证律师（Notary Public）服务
          </Link>
        </li>
        <li>
          <Link to="/zh/lgz" className="text-primary underline">
            文件认证与领事认证服务
          </Link>
        </li>
        <li>
          <Link to="/zh/corporate" className="text-primary underline">
            公司与商务法律文件服务
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容复核日期 {PUBLISHED}。本指南仅为规划用的一般信息，不构成个案法律意见。办理前请向受理机关确认最新要求。
      </p>
    </article>
  );
}
